import { Tag } from "antd";
import { Building2 } from "lucide-react";

import type { OPSUserRoleVO } from "@/api/accounts/type";
import type { OrganizationDetailVO } from "@/api/system/organizations/type";
import { ORGANIZATION_ORG_TYPE_META } from "@/config/system";
import { cn } from "@/lib/cn";

import styles from "./OrganizationList.module.css";

/** 组织侧栏列表 props */
interface OrganizationListProps {
  /** 员工绑定的组织列表 */
  organizations: OrganizationDetailVO[];
  /** 各组织下的角色映射 */
  rolesMap: Record<string, OPSUserRoleVO[]>;
  /** 当前选中的组织 */
  selectedOrg: OrganizationDetailVO | null;
  /** 选中组织回调 */
  onSelect: (org: OrganizationDetailVO) => void;
}

/**
 * 身份权限左侧组织列表
 * 展示组织名称、类型与下属角色
 */
export const OrganizationList = ({
  organizations,
  rolesMap,
  selectedOrg,
  onSelect,
}: OrganizationListProps) => {
  return (
    <div className={styles.root}>
      <div className="shrink-0 border-b bg-slate-50 px-4 py-2.5">
        <h4 className="text-sm font-semibold flex items-center gap-2 m-0">
          <Building2 className="w-4 h-4 text-slate-500" />
          实体列表
          {organizations.length > 0 && (
            <Tag className="text-[10px] m-0 ml-auto">{organizations.length}</Tag>
          )}
        </h4>
      </div>
      <div className={cn(styles.scrollArea, "space-y-1.5")}>
        {organizations.length > 0 ? (
          organizations.map((org) => {
            // 当前组织下的角色列表，用于展示角色名
            const orgRoles = rolesMap[org.orgId] ?? [];
            const isSelected = selectedOrg?.orgId === org.orgId;
            const orgMeta = ORGANIZATION_ORG_TYPE_META[org.orgType];
            const OrgIcon = orgMeta?.icon;
            // 角色名称拼接，如「服务员，收银员」
            const roleNames = orgRoles.map((role) => role.roleName).join("，");
            return (
              <div
                key={org.orgId}
                className={cn(
                  "relative p-3 rounded-lg cursor-pointer transition-all border",
                  isSelected
                    ? "bg-orange-50 border-orange-200 shadow-sm ring-1 ring-orange-100"
                    : "hover:bg-slate-50 border-transparent hover:border-slate-200",
                )}
                onClick={() => onSelect(org)}
              >
                {isSelected && (
                  <span className="absolute top-3 right-3 w-1.5 h-1.5 rounded-full bg-orange-500" />
                )}
                <div className="flex items-start gap-2.5">
                  {OrgIcon && (
                    <div
                      className={cn(
                        "w-9 h-9 rounded-lg flex items-center justify-center shrink-0",
                        orgMeta.bgColor,
                      )}
                    >
                      <OrgIcon className={cn("w-4 h-4", orgMeta.color)} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{org.orgName}</div>
                    {roleNames && (
                      <div className="text-xs text-slate-500 mt-0.5 truncate">{roleNames}</div>
                    )}
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-1 flex-wrap">
                  <Tag bordered className="text-[9px] m-0 px-1">
                    {org.orgTypeLabel || orgMeta?.shortLabel}
                  </Tag>
                </div>
              </div>
            );
          })
        ) : (
          <div className="py-6 text-center text-xs text-slate-400">暂无绑定组织</div>
        )}
      </div>
    </div>
  );
};
